/**
 * Wyeast Laboratories — the Oregon liquid-yeast house, catalogued in full from
 * wyeastlab.com: the year-round strains and the seasonal Private Collection
 * (`-PC`) releases, plus the wine, cider, mead, saké and spirits cultures.
 *
 * Wyeast publishes fermentation ranges in both °F and °C; the °C figure is
 * taken as published (PLAN §5 canon). Attenuation is apparent attenuation, and
 * `alcoholTolerancePercent` is Wyeast's own single % ABV figure, or the upper
 * end where they print a band.
 *
 * Flocculation uses the labs' shared five-step vocabulary. Wyeast's "Low
 * Medium" and "Medium High" map onto `medium-low` and `medium-high`; the
 * "Low - High" printed on the mixed cultures describes the several organisms
 * in the pack rather than one settling behaviour, and is recorded as `medium`.
 *
 * `sta1` is `true` only where Wyeast states the strain carries the gene. They
 * publish no negative results, so every other strain is `null` — unknown, not
 * safe. The wine, cider and mead strains carry no published attenuation at
 * all, so those entries are `representative` on that figure alone.
 */

import type { Yeast } from '../../schema/yeast';

/** Wyeast Laboratories cultures. @see Yeast */
export const wyeastYeasts: readonly Yeast[] = [
  {
    id: 'wyeast-1007',
    lab: 'Wyeast',
    productCode: '1007',
    name: 'German Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 20 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A German ale strain that ferments fast and finishes very dry, with almost no ester of its own. It is the classic alt and kölsch choice, and it will drop clear given a cold week at the end.',
    tags: ['german', 'alt', 'kolsch', 'clean', 'crisp', 'dry'],
    url: 'https://wyeastlab.com/product/german-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1010',
    lab: 'Wyeast',
    productCode: '1010',
    name: 'American Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 14, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A vigorous top-cropping wheat strain that leaves a dry, faintly tart beer and very little ester. Reach for it when you want the wheat and the hops to speak rather than the yeast.',
    tags: ['american', 'wheat', 'dry', 'tart', 'crisp', 'low-ester', 'top-cropping'],
    url: 'https://wyeastlab.com/product/american-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1026',
    lab: 'Wyeast',
    productCode: '1026-PC',
    name: 'British Cask Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 77 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A cask strain built for British bitters and hoppy IPAs: a good malt profile, a crisp and slightly tart finish, and light fruit at most. It clears without filtration, which is half the point of a cask beer.',
    tags: ['english', 'cask', 'bitter', 'ipa', 'malty', 'crisp'],
    url: 'https://wyeastlab.com/product/british-cask-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1028',
    lab: 'Wyeast',
    productCode: '1028',
    name: 'London Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A bold London strain with a distinctly mineral edge and a crisp, fruity finish. It attenuates hard, so it suits higher-gravity English ales where you want the beer to dry out rather than sit sweet.',
    tags: ['english', 'london', 'mineral', 'crisp', 'attenuative', 'strong-ale'],
    url: 'https://wyeastlab.com/product/london-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1056',
    lab: 'Wyeast',
    productCode: '1056',
    name: 'American Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'The American house strain: clean, crisp and low in ester, and content to get out of the way of malt and hops. Ferment it at the cool end for a mild citrus note; it usually needs cold conditioning or filtration to drop bright.',
    tags: ['clean', 'neutral', 'american', 'pale-ale', 'ipa', 'house-strain'],
    url: 'https://wyeastlab.com/product/american-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1084',
    lab: 'Wyeast',
    productCode: '1084',
    name: 'Irish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The Irish stout strain, faintly tart and slightly diacetyl-forward, with enough ester to add depth to a roasty grist. Give it a proper diacetyl rest and it settles into something rounder than a clean American ale.',
    tags: ['irish', 'stout', 'esters', 'diacetyl', 'malty', 'tart'],
    url: 'https://wyeastlab.com/product/irish-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1087',
    lab: 'Wyeast',
    productCode: '1087-PC',
    name: 'Wyeast Bohemian Ale Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A blend of ale strains chosen for a quick start and a clean drop, aimed at British and American ales alike. Sold before as Wyeast Ale Blend 1540-PC, and useful when you want dependability more than a signature.',
    tags: ['blend', 'english', 'american', 'balanced', 'fast-starting', 'flocculent'],
    url: 'https://wyeastlab.com/product/wyeast-bohemian-ale-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1098',
    lab: 'Wyeast',
    productCode: '1098',
    name: 'British Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A dry, crisp British strain with a mild tartness and a clean finish. It ferments and flocculates well over a wide range and suits bitters, milds and English pale ales.',
    tags: ['english', 'whitbread', 'dry', 'crisp', 'tart', 'versatile'],
    url: 'https://wyeastlab.com/product/british-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1099',
    lab: 'Wyeast',
    productCode: '1099',
    name: 'Whitbread Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'Softer and maltier than 1098, with a light fruitiness and a finish that stops well short of dry. It flocculates well enough to clear without filtration, and a cool fermentation keeps the esters restrained.',
    tags: ['english', 'whitbread', 'malty', 'fruity', 'flocculent', 'clearing'],
    url: 'https://wyeastlab.com/product/whitbread-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1187',
    lab: 'Wyeast',
    productCode: '1187',
    name: 'Ringwood Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The Ringwood strain: distinctly fruity, complex and malty, and famous for being slow to start and slow to finish. Plan for a full diacetyl rest, because this one genuinely needs it.',
    tags: ['english', 'ringwood', 'esters', 'malty', 'complex', 'diacetyl-rest'],
    url: 'https://wyeastlab.com/product/ringwood-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1203',
    lab: 'Wyeast',
    productCode: '1203-PC',
    name: 'Burton IPA Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 74 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A blend built for historic English IPA, keeping the yeast neutral so the water profile and pale malt carry the beer. Ester level follows your fermentation temperature and pitch rate, and it flocculates well enough for cask.',
    tags: ['english', 'ipa', 'burton', 'blend', 'cask', 'hop-forward'],
    url: 'https://wyeastlab.com/product/burton-ipa-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1214',
    lab: 'Wyeast',
    productCode: '1214',
    name: 'Belgian Abbey Style Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A Belgian abbey strain with a full ester profile and a touch of spice, made for dubbels and strong dark ales. It wants a warm finish, and it will keep working through a high-gravity wort if you let the temperature drift up.',
    tags: ['belgian', 'abbey', 'esters', 'spicy', 'strong-ale', 'warm'],
    url: 'https://wyeastlab.com/product/belgian-abbey-style-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1217',
    lab: 'Wyeast',
    productCode: '1217-PC',
    name: 'West Coast IPA',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A West Coast strain that attenuates thoroughly, tolerates a wide temperature range and drops well. It stays balanced and neutral, with a little ester if you ferment warm, which is what you want under a big hop charge.',
    tags: ['american', 'west-coast', 'ipa', 'clean', 'flocculent', 'attenuative'],
    url: 'https://wyeastlab.com/product/west-coast-ipa/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1272',
    lab: 'Wyeast',
    productCode: '1272',
    name: 'American Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'An American ale strain with a little more character than 1056: slightly nutty, faintly tart, and clean enough for any pale ale. It flocculates better than the Chico strain, so it clears sooner.',
    tags: ['american', 'clean', 'nutty', 'tart', 'flocculent', 'pale-ale'],
    url: 'https://wyeastlab.com/product/american-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1275',
    lab: 'Wyeast',
    productCode: '1275',
    name: 'Thames Valley Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 77 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Thames Valley strain for classic British bitter: rich and complex without much fruit, and clean through the finish. It is the quiet, well-balanced option among the English strains.',
    tags: ['english', 'thames-valley', 'bitter', 'clean', 'balanced', 'complex'],
    url: 'https://wyeastlab.com/product/thames-valley-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1318',
    lab: 'Wyeast',
    productCode: '1318',
    name: 'London Ale III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The London III strain that made hazy IPA possible: soft, fruity and low-attenuating, leaving body and a full mouthfeel behind. It floats a long time, so give it patience rather than cold-crashing early.',
    tags: ['english', 'london', 'fruity', 'soft', 'hazy', 'neipa'],
    url: 'https://wyeastlab.com/product/london-ale-iii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1332',
    lab: 'Wyeast',
    productCode: '1332',
    name: 'Northwest Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Pacific Northwest brewery strain that leaves a malty, mildly fruity beer with real depth. It flocculates hard and finishes short of dry, which suits ambers and porters better than a dry pale ale.',
    tags: ['american', 'northwest', 'malty', 'fruity', 'flocculent', 'complex'],
    url: 'https://wyeastlab.com/product/northwest-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1335',
    lab: 'Wyeast',
    productCode: '1335',
    name: 'British Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A classic British profile that flocculates hard and finishes crisp, clean and fairly dry. A dependable bitter and mild strain when you want malt character without much ester.',
    tags: ['english', 'malty', 'crisp', 'dry', 'flocculent', 'bitter'],
    url: 'https://wyeastlab.com/product/british-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1388',
    lab: 'Wyeast',
    productCode: '1388',
    name: 'Belgian Strong Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 13,
    sta1: null,
    description:
      'A Belgian strong-ale strain that attenuates deeply and finishes dry with a light tartness. It runs warm and stays in suspension, so plan on a long conditioning period before it drops.',
    tags: ['belgian', 'strong-ale', 'dry', 'tart', 'esters', 'warm'],
    url: 'https://wyeastlab.com/product/belgian-strong-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1450',
    lab: 'Wyeast',
    productCode: '1450',
    name: "Denny's Favorite 50",
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 76 },
    temperatureC: { min: 16, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      "Denny Conn's house strain, and unusual in leaving big mouthfeel and malt character without leaving the beer sweet. It flocculates poorly, so give it time or a fining before packaging.",
    tags: ['american', 'versatile', 'malty', 'mouthfeel', 'house-strain', 'low-floc'],
    url: 'https://wyeastlab.com/product/dennys-favorite-50/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1469',
    lab: 'Wyeast',
    productCode: '1469',
    name: 'West Yorkshire Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A West Yorkshire strain with a full, chewy malt character that still finishes dry, plus moderate nutty and stone-fruit esters. It flocculates reliably, which makes it a natural for cask bitters and milds.',
    tags: ['english', 'yorkshire', 'malty', 'nutty', 'stone-fruit', 'cask', 'flocculent'],
    url: 'https://wyeastlab.com/product/west-yorkshire-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1581',
    lab: 'Wyeast',
    productCode: '1581-PC',
    name: 'Belgian Stout',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 85 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A versatile Belgian strain that ferments to dryness with moderate esters and very little phenol. It was selected for Belgian stouts, and it handles Belgian specialty ales just as well.',
    tags: ['belgian', 'stout', 'dry', 'esters', 'versatile', 'attenuative'],
    url: 'https://wyeastlab.com/product/belgian-stout/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1728',
    lab: 'Wyeast',
    productCode: '1728',
    name: 'Scottish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 73 },
    temperatureC: { min: 13, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The Scottish ale strain, at home in strong malty ales and clean enough to serve as a house yeast. It works across an unusually wide temperature range, and the ester profile climbs steadily as you let it warm.',
    tags: ['scottish', 'malty', 'strong-ale', 'wee-heavy', 'versatile', 'wide-range'],
    url: 'https://wyeastlab.com/product/scottish-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1762',
    lab: 'Wyeast',
    productCode: '1762',
    name: 'Belgian Abbey Style Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A cleaner Belgian abbey strain that lets malt and alcohol carry a dark strong ale rather than burying them in ester. Push the temperature or the gravity and delicate dried-fruit notes appear.',
    tags: ['belgian', 'abbey', 'dark-strong', 'clean', 'dried-fruit', 'high-gravity'],
    url: 'https://wyeastlab.com/product/belgian-abbey-style-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1768',
    lab: 'Wyeast',
    productCode: '1768-PC',
    name: 'English Special Bitter',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A malt-forward English strain with soft nutty flavors and a neutral finish. It is close to 1968 London ESB but slightly less flocculent, so it still clears well without dropping out early.',
    tags: ['english', 'esb', 'bitter', 'malty', 'nutty', 'neutral'],
    url: 'https://wyeastlab.com/product/english-special-bitter/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1882',
    lab: 'Wyeast',
    productCode: '1882-PC',
    name: 'Thames Valley Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 16, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Thames Valley strain from a brewery that no longer exists, giving crisp, dry beers with a rich malt profile and moderate stone-fruit esters. It attenuates and flocculates well; give it a diacetyl rest before you cool it.',
    tags: ['english', 'thames-valley', 'crisp', 'dry', 'stone-fruit', 'flocculent'],
    url: 'https://wyeastlab.com/product/thames-valley-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-1968',
    lab: 'Wyeast',
    productCode: '1968',
    name: 'London ESB Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The London ESB strain, malty and fruity and famously quick to drop. It flocculates so hard that it can quit early in a tall fermenter, so rouse it if the gravity stalls.',
    tags: ['english', 'esb', 'malty', 'flocculent', 'cask', 'fruity'],
    url: 'https://wyeastlab.com/product/london-esb-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2000',
    lab: 'Wyeast',
    productCode: '2000-PC',
    name: 'Budvar Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The Budvar strain: a malty nose with subtle fruit, a rich malt palate, and a dry, well-balanced finish that lets late hop character through. A slow, cold fermentation and a long lagering period repay the trouble.',
    tags: ['lager', 'czech', 'budvar', 'malty', 'crisp', 'balanced'],
    url: 'https://wyeastlab.com/product/budvar-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2001',
    lab: 'Wyeast',
    productCode: '2001-PC',
    name: 'Pilsner Urquell H-Strain',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The Pilsner Urquell H-strain, with a mild fruit and floral aroma over a very dry, clean palate and a surprisingly full mouthfeel. It finishes neutral, which is exactly what a Bohemian pilsner asks for.',
    tags: ['lager', 'czech', 'pilsner-urquell', 'dry', 'clean', 'full-mouthfeel'],
    url: 'https://wyeastlab.com/product/pilsner-urquell-h-strain/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2002',
    lab: 'Wyeast',
    productCode: '2002-PC',
    name: 'Gambrinus Style Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Czech lager strain that keeps malt in front with subtle floral and fruit notes behind it. It finishes soft and smooth with a lingering maltiness rather than a snap.',
    tags: ['lager', 'czech', 'gambrinus', 'malty', 'full-bodied', 'smooth'],
    url: 'https://wyeastlab.com/product/gambrinus-style-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2007',
    lab: 'Wyeast',
    productCode: '2007',
    name: 'Pilsen Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A clean, neutral American pilsner strain that finishes crisp and gets out of the way. It is the safe choice when you want the malt bill and the hops judged on their own.',
    tags: ['lager', 'american', 'pilsner', 'clean', 'crisp', 'neutral'],
    url: 'https://wyeastlab.com/product/pilsen-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2035',
    lab: 'Wyeast',
    productCode: '2035-PC',
    name: 'American Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A complex, aromatic American lager strain that suits pre-Prohibition pilsners and adjunct lagers alike. It gives more aroma than the usual clean lager strains without tipping into ester.',
    tags: ['lager', 'american', 'pre-prohibition', 'complex', 'aromatic', 'adjunct'],
    url: 'https://wyeastlab.com/product/american-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2042',
    lab: 'Wyeast',
    productCode: '2042-PC',
    name: 'Danish Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Danish lager strain for Dortmunder-style beers, fermenting crisp and dry with a soft, rounded profile that pushes hop character forward. It flocculates poorly, so lager it properly before packaging.',
    tags: ['lager', 'danish', 'dortmunder', 'crisp', 'dry', 'hop-forward'],
    url: 'https://wyeastlab.com/product/danish-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2105',
    lab: 'Wyeast',
    productCode: '2105-PC',
    name: 'Rocky Mountain Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A cold-tolerant American lager strain for light pilsners and adjunct beers, finishing on the malt with a medium ester profile. It ferments well at the low end of the range where other strains stall.',
    tags: ['lager', 'american', 'rocky-mountain', 'malty', 'balanced', 'cold'],
    url: 'https://wyeastlab.com/product/rocky-mountain-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2112',
    lab: 'Wyeast',
    productCode: '2112',
    name: 'California Lager',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 14, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The California common strain, a lager yeast that ferments happily at ale temperatures and still finishes crisp. It flocculates hard and leaves a malty, faintly fruity beer, which is the whole steam-beer trick.',
    tags: ['california-common', 'steam', 'malty', 'crisp', 'flocculent', 'warm-lager'],
    url: 'https://wyeastlab.com/product/california-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2124',
    lab: 'Wyeast',
    productCode: '2124',
    name: 'Bohemian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 7, max: 20 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The 34/70-style German lager strain, clean and malty and workable across a remarkably wide temperature range. It is the default lager yeast for good reason: healthy pitch, diacetyl rest, and it rarely misbehaves.',
    tags: ['lager', 'german', 'clean', 'malty', 'wide-range', 'w34-70'],
    url: 'https://wyeastlab.com/product/bohemian-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2206',
    lab: 'Wyeast',
    productCode: '2206',
    name: 'Bavarian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Bavarian lager strain that leaves a rich, malty, full-bodied beer. It is the one to reach for when the malt is the point: helles, märzen and bock rather than a dry pilsner.',
    tags: ['lager', 'bavarian', 'malty', 'full-bodied', 'oktoberfest', 'bock'],
    url: 'https://wyeastlab.com/product/bavarian-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2247',
    lab: 'Wyeast',
    productCode: '2247-PC',
    name: 'European Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A clean, dry European lager strain that suits aggressively hopped pilsners. It throws the mild sulfur note typical of the classics, and it dissipates over a proper lagering period.',
    tags: ['lager', 'european', 'dry', 'clean', 'sulfur', 'pilsner'],
    url: 'https://wyeastlab.com/product/european-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2272',
    lab: 'Wyeast',
    productCode: '2272-PC',
    name: 'North American Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 11, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A traditional North American lager culture for light pilsners and adjunct beers, mildly malty with a medium ester profile. It flocculates hard, so it clears quickly once it is cold.',
    tags: ['lager', 'north-american', 'adjunct', 'malty', 'flocculent', 'balanced'],
    url: 'https://wyeastlab.com/product/north-american-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2278',
    lab: 'Wyeast',
    productCode: '2278',
    name: 'Czech Pils',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The Czech pilsner strain, finishing dry and malty and made for Bohemian-style beers. It throws sulfur during fermentation; ferment at the warm end of the range and condition it out.',
    tags: ['lager', 'czech', 'pilsner', 'dry', 'malty', 'sulfur'],
    url: 'https://wyeastlab.com/product/czech-pils/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2308',
    lab: 'Wyeast',
    productCode: '2308',
    name: 'Munich Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Munich lager strain that ferments smooth, well-rounded and full-bodied. It is prone to diacetyl, so a thorough rest at the end of primary is not optional here.',
    tags: ['lager', 'munich', 'smooth', 'full-bodied', 'diacetyl-rest', 'malty'],
    url: 'https://wyeastlab.com/product/munich-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2352',
    lab: 'Wyeast',
    productCode: '2352-PC',
    name: 'Munich Lager II',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 74 },
    temperatureC: { min: 11, max: 17 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Munich brewery strain that produces little diacetyl and little sulfur, which shortens the conditioning you would otherwise owe a lager. An easy choice for malt-driven beers.',
    tags: ['lager', 'munich', 'malty', 'low-diacetyl', 'low-sulfur', 'clean'],
    url: 'https://wyeastlab.com/product/munich-lager-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2487',
    lab: 'Wyeast',
    productCode: '2487-PC',
    name: 'Hella Bock Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'An Austrian alpine strain for rich, full-bodied malty lagers, attenuating well while leaving body behind. Let the temperature rise for a diacetyl rest at the end of primary before you cool it.',
    tags: ['lager', 'austrian', 'bock', 'malty', 'full-bodied', 'high-gravity'],
    url: 'https://wyeastlab.com/product/hella-bock-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2565',
    lab: 'Wyeast',
    productCode: '2565',
    name: 'Kölsch',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The kölsch strain: crisp and faintly fruity, fermenting like an ale and conditioning like a lager. It flocculates poorly and can be slow to clear, so give it a long cold rest.',
    tags: ['kolsch', 'german', 'crisp', 'fruity', 'low-floc', 'hybrid'],
    url: 'https://wyeastlab.com/product/klsch/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2575',
    lab: 'Wyeast',
    productCode: '2575-PC',
    name: 'Kölsch II',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A second kölsch strain from a German brewing school, richer than 2565 with a soft malt finish. It ferments well cold too, which makes it useful for quick lager-like beers.',
    tags: ['kolsch', 'german', 'malty', 'low-diacetyl', 'cold-tolerant', 'crisp'],
    url: 'https://wyeastlab.com/product/klsch-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2633',
    lab: 'Wyeast',
    productCode: '2633',
    name: 'Octoberfest Lager Blend',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A blend of lager strains aimed at a rich, malty, full-bodied Oktoberfest. It attenuates well while leaving plenty of malt and mouthfeel, and it stays low in sulfur.',
    tags: ['lager', 'oktoberfest', 'blend', 'malty', 'full-bodied', 'low-sulfur'],
    url: 'https://wyeastlab.com/product/octoberfest-lager-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-2782',
    lab: 'Wyeast',
    productCode: '2782-PC',
    name: 'Staro Prague Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A Bohemian lager strain giving medium to full body with moderate fruit and bready malt. It leans sweet, so bitter it a little harder than you would a drier pilsner strain.',
    tags: ['lager', 'czech', 'bohemian', 'malty', 'bready', 'full-bodied'],
    url: 'https://wyeastlab.com/product/staro-prague-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3031',
    lab: 'Wyeast',
    productCode: '3031-PC',
    name: 'Saison-Brett Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'Two saison strains and Brettanomyces together, giving earthy farmhouse spice alongside tropical and stone-fruit esters. It attenuates very deeply and carries STA1, so treat anything packaged with it as capable of drying out further.',
    tags: ['saison', 'brett', 'blend', 'dry', 'farmhouse', 'sta1', 'tropical'],
    url: 'https://wyeastlab.com/product/saison-brett-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3056',
    lab: 'Wyeast',
    productCode: '3056',
    name: 'Bavarian Wheat Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A blend of a neutral ale strain and a Bavarian wheat strain, which softens the banana and clove rather than removing it. The right choice when you want a hint of hefeweizen rather than the full statement.',
    tags: ['wheat', 'german', 'blend', 'banana', 'clove', 'subtle'],
    url: 'https://wyeastlab.com/product/bavarian-wheat-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3068',
    lab: 'Wyeast',
    productCode: '3068',
    name: 'Weihenstephan Weizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The Weihenstephan weizen strain, and the reference point for banana and clove. Pitch rate, gravity and temperature all move the balance, so decide which end you want before you set the fermenter.',
    tags: ['wheat', 'hefeweizen', 'banana', 'clove', 'german', 'low-floc'],
    url: 'https://wyeastlab.com/product/weihenstephan-weizen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3191',
    lab: 'Wyeast',
    productCode: '3191-PC',
    name: 'Berliner Weisse Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 6,
    sta1: null,
    description:
      'A German ale strain, Lactobacillus and Brettanomyces together for a true Berliner weisse. Fermentation starts slowly by design and the beer needs three to six months before the flavor resolves.',
    tags: ['berliner-weisse', 'lacto', 'brett', 'sour', 'blend', 'slow', 'aging'],
    url: 'https://wyeastlab.com/product/berliner-weisse-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3209',
    lab: 'Wyeast',
    productCode: '3209-PC',
    name: 'Oud Bruin Ale Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 27, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A sour blend built for dark, malt-accented styles: sharp acidity like 3763, but with the malt character left intact. It takes fruit in secondary well, cherries and raspberries especially.',
    tags: ['oud-bruin', 'sour', 'blend', 'malty', 'fruit', 'flanders'],
    url: 'https://wyeastlab.com/product/oud-bruin-ale-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3278',
    lab: 'Wyeast',
    productCode: '3278',
    name: 'Belgian Lambic Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'The lambic blend: a Belgian ale strain, a sherry strain, two Brettanomyces, Lactobacillus and Pediococcus in fixed proportions. Do not propagate it, because repitching changes the balance that makes it work.',
    tags: ['lambic', 'blend', 'brett', 'lacto', 'pedio', 'sour', 'aging'],
    url: 'https://wyeastlab.com/product/belgian-lambic-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3333',
    lab: 'Wyeast',
    productCode: '3333-PC',
    name: 'German Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A German wheat strain with the usual banana and clove balance but unusual flocculation, which is what makes kristallweizen possible without filtration. Over-pitching flattens the banana, so pitch on the light side.',
    tags: ['wheat', 'german', 'kristallweizen', 'banana', 'clove', 'flocculent'],
    url: 'https://wyeastlab.com/product/german-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3463',
    lab: 'Wyeast',
    productCode: '3463-PC',
    name: 'Forbidden Fruit',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A witbier and grand cru strain, spicy and phenolic with a complex ester profile behind it. The dry, faintly tart finish is what carries orange peel and coriander rather than smothering them.',
    tags: ['witbier', 'belgian', 'phenolic', 'spicy', 'tart', 'grand-cru'],
    url: 'https://wyeastlab.com/product/forbidden-fruit/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3522',
    lab: 'Wyeast',
    productCode: '3522',
    name: 'Belgian Ardennes',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The Ardennes strain, phenolic and spicy and content to run warm. It flocculates hard for a Belgian yeast, so it clears sooner than most of its neighbors.',
    tags: ['belgian', 'ardennes', 'phenolic', 'spicy', 'flocculent', 'warm'],
    url: 'https://wyeastlab.com/product/belgian-ardennes/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3638',
    lab: 'Wyeast',
    productCode: '3638',
    name: 'Bavarian Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Bavarian wheat strain with more going on than the standard: apple, pear and plum alongside the banana, and vanilla under the clove. Under-pitch and ferment warm to push the esters, over-pitch and the banana disappears.',
    tags: ['wheat', 'bavarian', 'banana', 'clove', 'vanilla', 'stone-fruit'],
    url: 'https://wyeastlab.com/product/bavarian-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3655',
    lab: 'Wyeast',
    productCode: '3655-PC',
    name: 'Belgian Schelde Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A top-fermenting strain from East Flanders that gives classic Belgian aroma over a full, malty body. It suits beers built on good pale and crystal malt rather than sugar.',
    tags: ['belgian', 'flanders', 'malty', 'smooth', 'full-bodied', 'complex'],
    url: 'https://wyeastlab.com/product/belgian-schelde-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3711',
    lab: 'Wyeast',
    productCode: '3711',
    name: 'French Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'The French saison strain, and the one that will take a beer drier than you planned. It carries STA1, so anything it touches can keep attenuating in the package.',
    tags: ['saison', 'french', 'dry', 'peppery', 'attenuative', 'sta1'],
    url: 'https://wyeastlab.com/product/french-saison/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3724',
    lab: 'Wyeast',
    productCode: '3724',
    name: 'Belgian Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'The Dupont saison strain, famous for stalling around 1.030 and then finishing if you let it get properly hot. It carries STA1 and will keep going, so give it heat and time rather than a new pitch.',
    tags: ['saison', 'belgian', 'dupont', 'phenolic', 'hot', 'stalls', 'sta1'],
    url: 'https://wyeastlab.com/product/belgian-saison/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3725',
    lab: 'Wyeast',
    productCode: '3725-PC',
    name: 'Bière de Garde',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A bière de garde strain, malty and full up front and dry and faintly tart at the finish, with subtle spice. It ferments without sluggishness and carries STA1, so it will keep drying out given time.',
    tags: ['biere-de-garde', 'malty', 'dry', 'tart', 'sta1', 'warm'],
    url: 'https://wyeastlab.com/product/bire-de-garde/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3726',
    lab: 'Wyeast',
    productCode: '3726',
    name: 'Farmhouse Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A farmhouse strain that runs hot like the classic saison yeasts and finishes dry. It flocculates better than 3724 while still wanting the same warm, patient fermentation.',
    tags: ['farmhouse', 'saison', 'dry', 'warm', 'esters', 'sta1'],
    url: 'https://wyeastlab.com/product/farmhouse-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3739',
    lab: 'Wyeast',
    productCode: '3739-PC',
    name: 'Flanders Golden Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A well-balanced northern Belgian strain giving moderate esters and spicy phenols over a dry finish with a hint of malt. It carries STA1 and works across a broad range of Belgian styles.',
    tags: ['belgian', 'golden-ale', 'esters', 'phenolic', 'dry', 'sta1', 'versatile'],
    url: 'https://wyeastlab.com/product/flanders-golden-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3763',
    lab: 'Wyeast',
    productCode: '3763',
    name: 'Roeselare Ale Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 80 },
    temperatureC: { min: 18, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'The Roeselare blend, and the shortest route to a Flanders red: earthy, complex and sour with a distinct pie-cherry note. It wants up to eighteen months, and it should not be propagated.',
    tags: ['roeselare', 'flanders-red', 'sour', 'blend', 'cherry', 'aging', 'oak'],
    url: 'https://wyeastlab.com/product/roeselare-ale-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3787',
    lab: 'Wyeast',
    productCode: '3787',
    name: 'Belgian High Gravity',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The Westmalle-style Trappist strain, phenolic and estery and able to take a high-gravity wort to a dry finish. It stays in suspension a long while and rewards a warm, slow rise.',
    tags: ['trappist', 'belgian', 'high-gravity', 'phenolic', 'esters', 'attenuative'],
    url: 'https://wyeastlab.com/product/belgian-high-gravity/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3789',
    lab: 'Wyeast',
    productCode: '3789-PC',
    name: 'Belgian Style Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 20, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Belgian Saccharomyces and Brettanomyces together, aimed at abbey-style beer from the Florenville region. Phenolics and spice build as the temperature rises, with a subdued classic Brett character behind them.',
    tags: ['belgian', 'brett', 'blend', 'abbey', 'phenolic', 'spicy', 'subtle-funk'],
    url: 'https://wyeastlab.com/product/belgian-style-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3822',
    lab: 'Wyeast',
    productCode: '3822-PC',
    name: 'Belgian Dark Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A high-acid Belgian strain whose balanced esters and phenols leave room for dark malt and candi sugar to show. Spicy, tart and dry through a complex finish, and it carries STA1.',
    tags: ['belgian', 'dark-ale', 'tart', 'spicy', 'dry', 'high-gravity', 'sta1'],
    url: 'https://wyeastlab.com/product/belgian-dark-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3864',
    lab: 'Wyeast',
    productCode: '3864-PC',
    name: 'Canadian/Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 79 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'An alcohol-tolerant strain for well-balanced Belgian abbey ales, with banana and fruit esters over mild phenolics and a hint of acidity. Raise the gravity or the temperature and the esters climb with it.',
    tags: ['belgian', 'abbey', 'banana', 'esters', 'phenolic', 'high-gravity'],
    url: 'https://wyeastlab.com/product/canadianbelgian-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3942',
    lab: 'Wyeast',
    productCode: '3942-PC',
    name: 'Belgian Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A small-brewery Belgian strain with moderate esters and very little phenol: apple, bubblegum and plum over malt and hops. It finishes dry with a hint of tartness.',
    tags: ['belgian', 'wheat', 'apple', 'plum', 'bubblegum', 'dry', 'tart'],
    url: 'https://wyeastlab.com/product/belgian-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-3944',
    lab: 'Wyeast',
    productCode: '3944',
    name: 'Belgian Witbier',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The Belgian witbier strain, mildly phenolic and tart, and versatile enough for spiced beers generally. It leaves enough acidity to keep a wheat beer refreshing rather than doughy.',
    tags: ['witbier', 'belgian', 'spicy', 'tart', 'phenolic', 'versatile'],
    url: 'https://wyeastlab.com/product/belgian-witbier/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-4007',
    lab: 'Wyeast',
    productCode: '4007',
    name: 'Malolactic Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'A malolactic blend of Oenococcus oeni for reducing malic acid in wine, chosen to cover a broad range of temperature and pH. Not a fermenting yeast: it converts acid after the primary is done.',
    tags: ['malolactic', 'wine', 'oenococcus', 'acid-reduction', 'blend'],
    url: 'https://wyeastlab.com/product/malolactic-blend/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4021',
    lab: 'Wyeast',
    productCode: '4021',
    name: 'Dry White/Sparkling',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'A white wine strain that ferments crisp and dry and flocculates well, which makes it a good base for sparkling wine. It is also used to finish barley wine in secondary.',
    tags: ['wine', 'white', 'sparkling', 'crisp', 'dry', 'barley-wine', 'flocculent'],
    url: 'https://wyeastlab.com/product/dry-whitesparkling/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4028',
    lab: 'Wyeast',
    productCode: '4028',
    name: 'Red',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 32 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A red wine strain giving Beaujolais-type fruitiness in wines that mature quickly, and enough structure for bigger reds that will age. It foams little and produces little sulfur across a broad temperature range.',
    tags: ['wine', 'red', 'beaujolais', 'fruity', 'low-sulfur', 'wide-range'],
    url: 'https://wyeastlab.com/product/red/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4114',
    lab: 'Wyeast',
    productCode: '4114',
    name: 'Er1A Malolactic',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 21, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'An Oenococcus oeni suspension selected for low-pH tolerance, which makes it the malolactic choice for high-acid reds. Conversion generally finishes within one to three months.',
    tags: ['malolactic', 'wine', 'red', 'oenococcus', 'low-ph', 'acid-reduction'],
    url: 'https://wyeastlab.com/product/er1a-malolactic/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4134',
    lab: 'Wyeast',
    productCode: '4134',
    name: 'Saké',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'The Saké #9 strain, used with koji for rice-based brewing. Full-bodied and silky on the palate with low ester production.',
    tags: ['sake', 'rice', 'koji', 'smooth', 'full-bodied', 'low-ester'],
    url: 'https://wyeastlab.com/product/sake/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4184',
    lab: 'Wyeast',
    productCode: '4184',
    name: 'Sweet Mead',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A sweet mead strain that leaves two to three percent residual sugar behind, with a rich fruity profile that suits fruit meads. It needs a proper nutrient regime to finish cleanly.',
    tags: ['mead', 'sweet', 'fruit', 'residual-sugar', 'cider'],
    url: 'https://wyeastlab.com/product/sweet-mead/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4221',
    lab: 'Wyeast',
    productCode: '4221',
    name: 'Ey2d Malolactic',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'An Oenococcus oeni suspension selected for tolerance to cool cellar temperatures, suggested for white wines. Malolactic conversion generally completes within one to three months.',
    tags: ['malolactic', 'wine', 'white', 'oenococcus', 'cool', 'acid-reduction'],
    url: 'https://wyeastlab.com/product/ey2d-malolactic/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4242',
    lab: 'Wyeast',
    productCode: '4242',
    name: 'Fruity White',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A white wine strain with very high ester formation, bready aroma and vanilla notes, chosen to let fruit character dominate. It finishes slightly sweet and soft rather than crisp.',
    tags: ['wine', 'white', 'fruity', 'esters', 'vanilla', 'soft-finish'],
    url: 'https://wyeastlab.com/product/fruity-white/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4244',
    lab: 'Wyeast',
    productCode: '4244',
    name: 'Italian Red',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A bold, well-rounded red wine strain with soft fruit character and a dry, crisp finish. Selected for Italian grape varieties, and seasonal in the Activator format.',
    tags: ['wine', 'red', 'italian', 'bold', 'fruity', 'dry'],
    url: 'https://wyeastlab.com/product/italian-red/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4267',
    lab: 'Wyeast',
    productCode: '4267',
    name: 'Summation Red',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A red wine strain with an intense berry and graham-cracker nose and a jammy, slightly vinous palate. It handles high-sugar musts well, which is why it turns up in fruit wines too.',
    tags: ['wine', 'red', 'berry', 'jammy', 'high-sugar', 'complex'],
    url: 'https://wyeastlab.com/product/summation-red/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4347',
    lab: 'Wyeast',
    productCode: '4347',
    name: 'Extreme Fermentation',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 21,
    sta1: null,
    description:
      'A very alcohol-tolerant strain for spirits washes and for restarting stuck fermentations. It runs clean, dry and low in ester, which is what you want when the result will be distilled.',
    tags: ['spirits', 'high-alcohol', 'stuck-fermentation', 'clean', 'dry', 'neutral'],
    url: 'https://wyeastlab.com/product/extreme-fermentation/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4632',
    lab: 'Wyeast',
    productCode: '4632',
    name: 'Dry Mead',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'The dry mead strain, low-foaming and producing little or no sulfur. It tolerates alcohol to eighteen percent, so feed it nutrient properly or it will stall on the way there.',
    tags: ['mead', 'dry', 'low-sulfur', 'high-alcohol', 'nutrient'],
    url: 'https://wyeastlab.com/product/dry-mead/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4766',
    lab: 'Wyeast',
    productCode: '4766',
    name: 'Cider',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A cider strain that ferments crisp and dry and finishes big and fruity, letting the apple or pear character lead. It suits perry and other fruit ferments equally.',
    tags: ['cider', 'crisp', 'dry', 'fruity', 'apple', 'pear'],
    url: 'https://wyeastlab.com/product/cider/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4767',
    lab: 'Wyeast',
    productCode: '4767',
    name: 'Dry/Fortified',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A wine strain with a mild toast and vanilla nose and a very dry finish, used for dry reds and whites and for classic ports once brandy is added. It handles high-sugar musts.',
    tags: ['wine', 'fortified', 'port', 'dry', 'toast', 'vanilla'],
    url: 'https://wyeastlab.com/product/dryfortified/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4783',
    lab: 'Wyeast',
    productCode: '4783',
    name: 'Sweet White',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 13, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A white wine strain that produces a distinct Riesling character: rich and creamy with a dry finish and a hint of sweetness in the aftertaste.',
    tags: ['wine', 'white', 'riesling', 'creamy', 'fruity', 'off-dry'],
    url: 'https://wyeastlab.com/product/sweet-white/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-4946',
    lab: 'Wyeast',
    productCode: '4946',
    name: 'Bold Red/High Alcohol',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 16, max: 29 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'A dominating red wine strain tolerant to eighteen percent alcohol, built for Zinfandel, Pinot Noir, Syrah and any high-sugar must. It is also the usual choice for restarting a stuck fermentation.',
    tags: ['wine', 'red', 'high-alcohol', 'zinfandel', 'stuck-fermentation', 'strong'],
    url: 'https://wyeastlab.com/product/bold-redhigh-alcohol/',
    provenance: 'representative',
  },
  {
    id: 'wyeast-5112',
    lab: 'Wyeast',
    productCode: '5112',
    name: 'Brettanomyces bruxellensis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Brettanomyces bruxellensis, the classic barnyard-and-leather funk, used in secondary alongside a primary yeast. It works slowly and rewards months rather than weeks.',
    tags: ['brett', 'brettanomyces', 'wild', 'secondary', 'funk', 'aging'],
    url: 'https://wyeastlab.com/product/brettanomyces-bruxellensis/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-5151',
    lab: 'Wyeast',
    productCode: '5151-PC',
    name: 'Brettanomyces claussenii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 80 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The mildest of the Bretts, with tropical fruit and pineapple rather than barnyard. It ferments best after primary has begun and the pH has dropped, and it may throw a pellicle in the package.',
    tags: ['brett', 'claussenii', 'tropical', 'pineapple', 'mild-funk', 'secondary'],
    url: 'https://wyeastlab.com/product/brettanomyces-claussenii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-5223',
    lab: 'Wyeast',
    productCode: '5223-PC',
    name: 'Lactobacillus brevis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Lactobacillus brevis that tolerates more IBU than most souring bacteria, and produces alcohol alongside lactic acid. Useful for kettle souring and for remixing a Flanders red or lambic profile.',
    tags: ['lacto', 'brevis', 'sour', 'hop-tolerant', 'kettle-sour', 'berliner-weisse'],
    url: 'https://wyeastlab.com/product/lactobacillus-brevis/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-5335',
    lab: 'Wyeast',
    productCode: '5335',
    name: 'Lactobacillus buchneri',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Lactobacillus buchneri giving moderate acidity, found in gueuze, lambics, sour browns and Berliner weisse. It is hop-sensitive, so keep the wort under 10 IBU and pair it with a Saccharomyces strain.',
    tags: ['lacto', 'buchneri', 'sour', 'gueuze', 'lambic', 'hop-sensitive'],
    url: 'https://wyeastlab.com/product/lactobacillus-buchneri/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-5526',
    lab: 'Wyeast',
    productCode: '5526',
    name: 'Brettanomyces lambicus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The lambic Brettanomyces, giving a pie-cherry flavor and sourness alongside classic Brett character. It needs three to six months and other cultures alongside it to reach the Belgian profile.',
    tags: ['brett', 'lambicus', 'cherry', 'sour', 'aging', 'pellicle'],
    url: 'https://wyeastlab.com/product/brettanomyces-lambicus/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-5733',
    lab: 'Wyeast',
    productCode: '5733',
    name: 'Pediococcus damnosus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'Pediococcus damnosus, for Belgian styles that want acidity beyond what Lactobacillus gives. Expect acid to build with storage, and expect a ropy phase that Brettanomyces will eventually clean up.',
    tags: ['pedio', 'pediococcus', 'sour', 'gueuze', 'ropiness', 'aging'],
    url: 'https://wyeastlab.com/product/pediococcus-damnosus/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wyeast-9097',
    lab: 'Wyeast',
    productCode: '9097-PC',
    name: 'Old Ale Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'An attenuative English ale strain with a small charge of Brettanomyces, built for dark, fruity old ales. The Brett adds pie-cherry sourness slowly, over a long conditioning period.',
    tags: ['old-ale', 'english', 'brett', 'blend', 'fruity', 'cherry', 'aging'],
    url: 'https://wyeastlab.com/product/old-ale-blend/',
    provenance: 'spec-sheet',
  },
];
